// src/refracted Dashboard/MediaCards.js - Updated version
import React from "react";
import { usePlayerContext } from "../../hooks/PlayerContext";
import logger from "../../services/logger";

const MediaCards = () => {
  const {
    state, playTrack, setPlaylist, makeAuthenticatedRequest,
    toggleRadioMode, setPlaybackState
  } = usePlayerContext();
  const { mediaData, platform } = state;

  if (!mediaData) return null;

  // Helper to handle playing a track
  const handleTrackPlay = (track) => {
    // Format track for playback
    const formattedTrack = {
      uri: track.uri,
      name: track.name,
      artists: track.artists?.[0]?.name || '',
      images: track.album?.images || [],
    };
    
    playTrack(formattedTrack);
  };

  // Load and normalize tracks from a playlist via Spotify API
  const loadPlaylistTracks = async (playlist) => {
    const response = await makeAuthenticatedRequest(
      `/api/spotify/playlist/${playlist.id}`,
      { method: 'GET' },
      'spotify'
    );

    const data = await response.json();

    let items = null;
    if (data.tracks && data.tracks.items) {
      items = data.tracks.items;
    } else if (data.items) {
      items = data.items;
    } else {
      throw new Error(`No track items found in response: ${JSON.stringify(Object.keys(data))}`);
    }

    if (!items || items.length === 0) {
      throw new Error('Playlist appears to be empty');
    }

    return items.map((item, index) => {
      const t = item.track || item;
      if (!t || !t.uri) {
        logger.warn(`Invalid track at index ${index}:`, item);
        return null;
      }
      return {
        uri: t.uri,
        name: t.name || 'Unknown Track',
        artists: (t.artists || []).map(a => a.name).join(', ') || 'Unknown Artist',
        album: t.album?.name || 'Unknown Album',
        images: t.album?.images || [],
        popularity: t.popularity || 0,
        _uniqueId: `track-${t.uri}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
      };
    }).filter(Boolean);
  };

  // Play Now or Play with Radio Mode
  const handlePlaylistPlay = async (playlist, mode = 'normal') => {
    try {
      logger.platform('Loading playlist:', playlist.name, 'ID:', playlist.id);
      const tracks = await loadPlaylistTracks(playlist);
      logger.success(`Processed ${tracks.length} valid tracks from playlist`);

      setPlaylist({ name: playlist.name || 'Unknown Playlist', tracks }, { savePrevious: true });
      setPlaybackState(true);

      if (mode === 'radio' && !state.radioModeActive) {
        toggleRadioMode();
      }
    } catch (err) {
      logger.error("Couldn't load playlist:", err);
    }
  };

  // Render Spotify content
  if (platform === "spotify" && mediaData.topTracks) {
    return (
      <div className="dashboard-content-section">
        {/* Top Tracks Section */}
        <div className="content-section">
          <div className="section-header">
            <h2>Your Top Tracks</h2>
            <button className="see-all-button">See all</button>
          </div>
          
          <div className="media-grid">
            {mediaData.topTracks.slice(0, 6).map((track, i) => (
              <div className="media-item" key={i}>
                <div className="media-cover-art">
                  <img 
                    src={track.album?.images?.[0]?.url || "/default-album.png"} 
                    alt={track.name} 
                  />
                  <div className="media-play-overlay">
                    <button
                      className="play-button"
                      onClick={() => handleTrackPlay(track)}
                      aria-label={`Play ${track.name}`}
                    >
                      ▶
                    </button>
                  </div>
                </div>
                <div className="media-info">
                  <h3 className="media-title">{track.name}</h3>
                  <p className="media-subtitle">{track.artists?.[0]?.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Playlists Section */}
        <div className="content-section">
          <div className="section-header">
            <h2>Your Playlists</h2>
            <button className="see-all-button">See all</button>
          </div>
          
          <div className="media-grid">
            {mediaData.playlists.slice(0, 6).map((playlist, i) => (
              <div className="media-item" key={i}>
                <div className="media-cover-art">
                  <img 
                    src={playlist.images?.[0]?.url || "/default-playlist.png"} 
                    alt={playlist.name} 
                  />
                  <div className="media-play-overlay">
                    <button
                      className="play-button"
                      onClick={() => handlePlaylistPlay(playlist, 'normal')}
                      aria-label={`Play ${playlist.name}`}
                    >
                      ▶
                    </button>
                  </div>
                  <div className="track-actions">
                    <button className="track-action-button" aria-label="More options">•••</button>
                    <div className="hover-menu">
                      <button onClick={() => handlePlaylistPlay(playlist, 'radio')}>
                        🎙️ Play with Radio Mode
                      </button>
                    </div>
                  </div>
                </div>
                <div className="media-info">
                  <h3 className="media-title">{playlist.name}</h3>
                  <p className="media-subtitle">{playlist.tracks.total} tracks</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render YouTube content 
  if (platform === "youtube" && Array.isArray(mediaData)) {
    return (
      <div className="dashboard-content-section">
        <div className="content-section">
          <div className="section-header">
            <h2>Your Recent Videos</h2>
            <button className="see-all-button">See all</button>
          </div>
          
          <div className="media-grid">
            {mediaData.map((video, i) => (
              <div className="media-item" key={i}>
                <div className="media-cover-art">
                  <img
                    src={video.snippet?.thumbnails?.medium?.url || "/default-video.png"}
                    alt={video.snippet?.title}
                  />
                  <div className="media-play-overlay">
                    <button
                      className="play-button"
                      onClick={() => {
                        const formattedVideo = {
                          name: video.snippet?.title,
                          artists: video.snippet?.channelTitle,
                          images: [{ url: video.snippet?.thumbnails?.high?.url }],
                          snippet: video.snippet,
                          id: video.id
                        };
                        
                        playTrack(formattedVideo);
                      }}
                      aria-label={`Play ${video.snippet?.title}`}
                    >
                      ▶
                    </button>
                  </div>
                </div>
                <div className="media-info">
                  <h3 className="media-title">{video.snippet?.title}</h3>
                  <p className="media-subtitle">
                    {new Date(video.snippet?.publishedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return <p>No media data found.</p>;
};

export default MediaCards;